// Site display cells — the SHARED renderers for a site row's identity, type, solution
// glyph (grid · solar · gen + the grid-backup shield), tags, status, and its area's one line. In
// components/patterns (not a page) so ANY surface showing a site — list · detail · peek ·
// future assets/monitoring — renders them identically: evolve here, it evolves for every
// consumer (anti-drift, ADR-056 UX contract).

import { useState } from 'react';
import type { TFunction } from 'i18next';
import { Badge, type BadgeAppearance, type BadgeVariant } from '../ui/Badge.js';
import { ObservationMark, ObservationMarkRow, kindMarkSummaries } from './observations.js';
import { CONFIG_FIELD_META, siteObservations } from './solutionConfig.js';
import { EntityRef } from './EntityRef.js';
import { InfoPopover } from '../ui/InfoPopover.js';
import { icons } from '../ui/icons.js';
import { countryName, formatNumber } from '../../lib/format.js';
import type { SiteListItem, SolutionSummary } from '@ems-app/schemas/sites/responses';

// draft / active / decommissioned stay calm (subtle); suspended is the one bold
// pill (a recoverable PAUSE, warning/amber — it resumes + keeps its seat, ADR-055).
const STATUS_BADGE: Record<SiteListItem['status'], { variant: BadgeVariant; appearance: BadgeAppearance }> = {
  draft: { variant: 'neutral', appearance: 'subtle' },
  active: { variant: 'success', appearance: 'subtle' },
  suspended: { variant: 'warning', appearance: 'bold' },
  decommissioned: { variant: 'neutral', appearance: 'subtle' },
};

export function SiteStatusBadge({ status, t, appearance }: { status: SiteListItem['status']; t: TFunction; appearance?: BadgeAppearance }) {
  const b = STATUS_BADGE[status];
  const label = t(`status.${status}`);
  return (
    <Badge variant={b.variant} appearance={appearance ?? b.appearance} title={appearance === 'dot' ? label : undefined}>
      {label}
    </Badge>
  );
}

// The phone-compact status: a distinct icon + semantic color per status (the full text
// badge is too wide for the Site·Type·Status triad at <md). draft = in-setup (pencil),
// active = success, suspended = paused, decommissioned = archived.
const STATUS_GLYPH: Record<SiteListItem['status'], { Icon: typeof icons.statusSuccess; className: string }> = {
  draft: { Icon: icons.edit, className: 'text-[var(--color-text-secondary)]' },
  active: { Icon: icons.statusSuccess, className: 'text-[var(--color-success)]' },
  suspended: { Icon: icons.statusPaused, className: 'text-[var(--color-warning)]' },
  decommissioned: { Icon: icons.statusArchived, className: 'text-[var(--color-text-disabled)]' },
};

/** Compact status glyph (phone) — icon + color, the status name as the accessible label.
 *  The full text badge (`SiteStatusBadge`) rides md+; this keeps the triad fitting on a phone. */
export function SiteStatusGlyph({ status, t }: { status: SiteListItem['status']; t: TFunction }) {
  const { Icon, className } = STATUS_GLYPH[status];
  return (
    <span className={`inline-flex ${className}`} title={t(`status.${status}`)}>
      <Icon size={18} aria-label={t(`status.${status}`)} />
    </span>
  );
}

/** The glyph inputs from a list payload's solution summary — the one seam between
 *  the wire shape and the shared v2 renderer (strip · name · panel). */
export function glyphSourcesFromSummary(s: SolutionSummary): SolutionGlyphSources {
  return {
    grid: s.sources.includes('grid'),
    solar: s.sources.includes('solar'),
    generator: s.sources.includes('generator'),
    providerGenerator: s.sources.includes('provider_generator'),
    providerAutoFailover: s.provider_generator_auto_failover,
    wind: s.sources.includes('wind'),
  };
}

/** The site's Solution cell: the v2 glyph strip (one glyph per present supply
 *  source — ADR-056 §6) with the exact-set topology name as the accessible label;
 *  the strip is a tap target — the full breakdown opens behind it (a click, a
 *  tap; a sheet on a phone). ONE renderer for the list, peek, and detail
 *  (anti-drift). No config → muted "No configuration". */
export function SolutionCell({ solution, t }: { solution: SolutionSummary | null; t: TFunction }) {
  if (!solution) return <span className="text-sm text-[var(--color-text-disabled)]">{t('solution.none')}</span>;
  const sources = glyphSourcesFromSummary(solution);
  return (
    <InfoPopover label={t('solution.panel')} placement="top start" trigger={<SolutionGlyphStrip sources={sources} t={t} />}>
      <SolutionDetails solution={solution} t={t} />
    </InfoPopover>
  );
}

/** The Solution breakdown — one row per present source, each with its declared
 *  values (the provider generator shows its name + 24/7 — its capacity is the
 *  provider's asset), then cooling (units + site-total kW) and power. */
function SolutionDetails({ solution, t }: { solution: SolutionSummary; t: TFunction }) {
  const rows: { label: string; value: string }[] = [];
  const push = (label: string, ...parts: (string | null | undefined)[]) =>
    rows.push({ label, value: parts.filter(Boolean).join(' · ') || '—' });
  if (solution.sources.includes('grid'))
    push(t('solution.sources.grid'), solution.grid_provider, solution.grid_capacity_kw != null ? `${formatNumber(solution.grid_capacity_kw)} kW` : null);
  if (solution.sources.includes('solar'))
    push(t('solution.sources.solar'), solution.solar_capacity_kwp != null ? `${formatNumber(solution.solar_capacity_kwp)} kWp` : null);
  if (solution.sources.includes('generator'))
    push(t('solution.sources.generator'), solution.generator_capacity_kva != null ? `${formatNumber(solution.generator_capacity_kva)} kVA` : null, solution.generator_units != null ? t('solution.units', { count: solution.generator_units }) : null, solution.generator_tank_l != null ? t('solution.tank', { l: `${formatNumber(solution.generator_tank_l)} L` }) : null);
  if (solution.sources.includes('provider_generator'))
    push(t('solutionType.source.providerGen'), solution.provider_generator_name, solution.provider_generator_auto_failover ? t('solutionType.sla247') : null);
  if (solution.sources.includes('battery'))
    push(t('solution.sources.battery'), solution.battery_chemistry ? t(`solution.chemistry.${solution.battery_chemistry}`) : null, solution.battery_capacity_ah != null ? `${formatNumber(solution.battery_capacity_ah)} Ah` : null);
  if (solution.sources.includes('wind')) push(t('solution.sources.wind'));
  if (solution.has_cooling)
    push(
      t('detail.fields.cooling'),
      solution.cooling_units != null ? t('solution.units', { count: solution.cooling_units }) : null,
      solution.cooling_capacity_kw != null ? `${formatNumber(solution.cooling_capacity_kw)} kW` : null,
    );
  if (solution.power_system_capacity_kw != null) push(t('detail.fields.powerSystem'), `${formatNumber(solution.power_system_capacity_kw)} kW`);
  return (
    <div className="min-w-44">
      <div className="mb-1 font-semibold">{solutionNameV2(glyphSourcesFromSummary(solution), t)}</div>
      <dl className="space-y-0.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-baseline justify-between gap-4">
            <dt className="opacity-70">{r.label}</dt>
            <dd className="tabular-nums" dir="ltr">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ═══ Reconciliation cockpit cells — the config work-queue chip, the
// per-cell flags, and the v2 source-glyph grammar (ADR-056 §6). Simplified
// model: discovery SUGGESTS updates (open) which are used or ignored; completeness
// gaps count; the register itself holds only real, versioned periods. Consumed by
// the Workbench judging stories first; the live Solution view rewires to these
// when the backend payload lands. ═══

/** THE OBSERVATIONS CELL — the sites half of the shared grammar (ADR-061 §5).
 *  Replaces the private Review / Incomplete / OK pill, whose one-bucket-per-row
 *  shape fused a conflict and a missing into a single "N fields" count and named
 *  neither. The row shows its MOST SEVERE observation and counts the rest; the
 *  peek lists them all with both sides.
 *
 *  A clean row renders NOTHING (architect-ruled): on a surface whose job is to
 *  surface work, a green tick on ninety percent of rows is noise that hides the
 *  signal. Absence of a mark IS "fine".
 */
export function SiteObservationsCell({ item, face = 'solution', t }: { item: SiteListItem; face?: 'registry' | 'solution'; t: TFunction }) {
  const found = siteObservations(item, face);
  if (found.length === 0) return <span className="text-[var(--color-text-disabled)]">—</span>;
  // The T re-cut of the row face: counted PER-KIND marks, no in-cell words —
  // the subjects ride title + sr-only, the peek carries the cards. (The earlier
  // no-counts ruling banned a TOTAL riding the worst glyph — a per-kind count
  // on a per-kind mark cannot lie about the mix, which dissolves that concern.)
  const marks = kindMarkSummaries(found);
  const label = marks
    .map((m) => {
      const words = found
        .filter((o) => o.kind === m.kind)
        .map((o) => (o.field ? t(CONFIG_FIELD_META[o.field].labelKey) : t(`observations.family.${o.family}`)));
      return `${t(`observations.kind.${m.kind}`)}: ${words.join(' · ')}`;
    })
    .join(' — ');
  return <ObservationMarkRow marks={marks} label={label} />;
}

/** The column's DEGRADED face — the bare mark, so the signal never changes
 *  address when the box gets tight. */
export function SiteObservationsMark({ item, face = 'solution', t }: { item: SiteListItem; face?: 'registry' | 'solution'; t: TFunction }) {
  const found = siteObservations(item, face);
  const top = found[0];
  if (!top) return null;
  return (
    <span title={t(`observations.kind.${top.kind}`)}>
      <ObservationMark kind={top.kind} impact={top.impact} count={found.length} bare />
    </span>
  );
}

// ── The v2 source-glyph grammar (ADR-056 §6, locked): one glyph per PRESENT supply
// source — grid = plug · solar = sun · on-site generator = engine (own DG + tank,
// never the provider genset) · provider generator = SHIELD (+ a small 24/7 tag when
// auto-failover) · wind — battery never in the strip (near-universal, no signal). ──

export interface SolutionGlyphSources {
  grid: boolean;
  solar: boolean;
  /** On-site generator (own DG + tank). */
  generator: boolean;
  /** Provider generator — a shared genset backing the site (its capacity is the provider's asset). */
  providerGenerator: boolean;
  /** Auto-failover — the provider genset takes over whenever supply drops (the 24/7 tag). */
  providerAutoFailover: boolean;
  wind: boolean;
}

/** The topology NAME — the EXACT set of present supply sources, compositional, in
 *  canonical order (Grid · Solar · Gen · Backup gen · Wind); a single source gets
 *  "only"; MODIFIERS never appear (24/7 is a panel/filter fact, never a category).
 *  The strip's accessible label; also the group-band vocabulary (ADR-056 §6). */
export function solutionNameV2(s: SolutionGlyphSources, t: TFunction): string {
  const present: string[] = [];
  if (s.grid) present.push(t('solutionType.source.grid'));
  if (s.solar) present.push(t('solutionType.source.solar'));
  if (s.generator) present.push(t('solutionType.source.generator'));
  if (s.providerGenerator) present.push(t('solutionType.source.providerGen'));
  if (s.wind) present.push(t('solutionType.source.wind'));
  if (present.length === 0) return t('solutionType.none');
  if (present.length === 1) return t('solutionType.only', { source: present[0] });
  return present.join(' + ');
}

/** The provider-generator shield, with the 24/7 tag stacked under it when auto-failover. */
function ProviderGenGlyph({ autoFailover, t }: { autoFailover: boolean; t: TFunction }) {
  if (!autoFailover) return <icons.sourceProviderGen size={18} aria-hidden />;
  return (
    <span className="inline-flex flex-col items-center leading-none">
      <icons.sourceProviderGen size={15} aria-hidden />
      <span className="mt-px text-[8px] font-semibold tabular-nums" dir="ltr">{t('solutionType.sla247')}</span>
    </span>
  );
}

/** The v2 glyph strip. Decorative glyphs + an sr-only topology name; the consumer
 *  puts it behind an `InfoPopover` (the full breakdown) exactly as `SolutionCell` does. */
export function SolutionGlyphStrip({ sources, t }: { sources: SolutionGlyphSources; t: TFunction }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden className="inline-flex items-center gap-1.5 text-[var(--color-text-secondary)]">
        {sources.grid && <icons.sourceGrid size={18} aria-hidden />}
        {sources.solar && <icons.sourceSolar size={18} aria-hidden />}
        {sources.generator && <icons.sourceGenerator size={18} aria-hidden />}
        {sources.providerGenerator && <ProviderGenGlyph autoFailover={sources.providerAutoFailover} t={t} />}
        {sources.wind && <icons.sourceWind size={18} aria-hidden />}
      </span>
      <span className="sr-only">{solutionNameV2(sources, t)}</span>
    </span>
  );
}


/** A copiable reference code: the WHOLE code is a click-to-copy button (stops the
 *  row's peek-open), the icon swaps to a check on copy (no toast). The icon reveals
 *  on hover/focus of the code by default (list density); pass `iconAlways` for a
 *  detail surface (peek) where it should always show. Shared by the list identity
 *  cell + alt-ref column and the site peek. */
export function CopyableCode({
  value,
  t,
  iconAlways = false,
}: {
  value: string;
  t: TFunction;
  iconAlways?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={t('row.copyRef')}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="group/copy inline-flex min-w-0 items-center gap-1 rounded text-[var(--color-text-secondary)] outline-none transition-colors hover:text-[var(--color-text-primary)] focus-ring cursor-pointer"
    >
      <span className="truncate font-mono text-xs" dir="ltr">
        {value}
      </span>
      {copied ? (
        <icons.check size={14} aria-hidden className="shrink-0 text-[var(--color-success)]" />
      ) : (
        <icons.copy
          size={14}
          aria-hidden
          className={
            iconAlways
              ? 'shrink-0'
              : 'shrink-0 opacity-0 transition group-hover/copy:opacity-100 group-focus/copy:opacity-100'
          }
        />
      )}
    </button>
  );
}

/** The Site identity cell — the always-visible scan anchor: the follow star + the name
 *  link + the priority chip (if set), and the primary reference folded onto a copyable
 *  second line (ADR-057 Registry model). Placement (indoor/outdoor) rides the Type cell.
 *  Prop is the identity slice only, so every site-showing surface (Registry, the
 *  reconciliation cockpit, stories) renders the SAME cell. */
export function SiteIdentityCell({ site, to, t }: { site: Pick<SiteListItem, 'name' | 'reference' | 'site_priority' | 'status'>; to: string; t: TFunction }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {/* THE LEADING STATUS GLYPH — shown below lg, hidden above it.
          The two triggers cannot be made to agree: this is keyed to the VIEWPORT
          and the Status column drops by CONTAINER, and coupling them would need
          the cell to know the engine's decision while changing the width that
          decision is made from (a loop). So the breakpoint is chosen to fail in
          the SAFE direction — architect's ruling: a band where BOTH show is
          acceptable, a band where NEITHER does is not. `lg` sits comfortably
          above every width at which the column actually yields, so the overlap is
          a few hundred pixels and the gap is none.
          The follow star lives in its own first column, never in this slot. */}
      <span className="shrink-0 lg:hidden"><SiteStatusGlyph status={site.status} t={t} /></span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <EntityRef to={to} name={site.name} quiet className="max-w-[9rem] md:max-w-48 xl:max-w-none" />
          {site.site_priority && (
            <span className="hidden shrink-0 md:inline-flex">
              <Badge variant="neutral" appearance="subtle">{site.site_priority.name}</Badge>
            </span>
          )}
        </div>
        {/* Reference folded under the NAME (aligned with it, not the star), copyable on hover. */}
        {site.reference && <div className="mt-0.5"><CopyableCode value={site.reference} t={t} /></div>}
      </div>
    </div>
  );
}

/** The Type cell: the site-type name with the placement (indoor / outdoor) folded onto a
 *  muted second line (mirrors Cluster + zone). Always visible — the mobile triad is
 *  Site · Type · Status. */
export function SiteTypeCell({ site, t }: { site: SiteListItem; t: TFunction }) {
  return (
    <div className="min-w-0">
      <span>{site.site_type ? site.site_type.name : <span className="text-[var(--color-text-disabled)]">—</span>}</span>
      <div className="truncate text-xs text-[var(--color-text-secondary)]">
        {t(`detail.indoorValue.${site.is_indoor}`)}
      </div>
    </div>
  );
}

// The Registry tags cell caps at this many names, then "+N more"; the full set lives on
// the row peek / detail page.
const MAX_ROW_TAGS = 3;

/** The Tags cell — labels are METADATA, so in the dense list they read as a quiet,
 *  tag-glyph-led muted line (NOT the filled `Tag` chip, which would mimic the adjacent
 *  status Badge — two neutral pills competing). Capped at MAX_ROW_TAGS + a "+N more".
 *  The full removable chips live on the detail page, where they don't sit beside a status. */
export function SiteTagsCell({ tags, t }: { tags: SiteListItem['tags']; t: TFunction }) {
  if (tags.length === 0) return <span className="text-[var(--color-text-disabled)]">—</span>;
  const shown = tags.slice(0, MAX_ROW_TAGS);
  const extra = tags.length - shown.length;
  return (
    <div className="flex min-w-0 items-center gap-1.5 text-[var(--color-text-secondary)]">
      <icons.tag size={13} weight="fill" aria-hidden className="shrink-0 opacity-50" />
      <span className="truncate text-xs">
        {shown.map((tag) => tag.name).join(', ')}
        {extra > 0 && <span className="opacity-70"> {t('row.moreTags', { n: extra })}</span>}
      </span>
    </div>
  );
}

/** A site's administrative area on the record, on ONE line: its name, then where it sits — the areas above it,
 *  nearest first, out to the site's country ("Bint Jbeil · Nabatieh › Lebanon"). */
export function areaLine(area: { name: string; path: readonly string[] }, countryCode: string | null, lang: string): string {
  const around = [...area.path].reverse();
  if (countryCode) around.push(countryName(countryCode, lang));
  return around.length > 0 ? `${area.name} · ${around.join(' › ')}` : area.name;
}
